import Storage from 'expo-sqlite/kv-store';

import {
  isRouteCoordinate,
  type ComputedRoutePlan,
  type RouteCoordinate,
  type RouteRoutingProfile,
  type RouteUnitSystem,
  type RouteWaypoint,
} from '@sixam/shared/route-planner-core';

export type RoutePlannerDraft = Readonly<{
  autoRouting: boolean;
  center: RouteCoordinate;
  mapLayer: 'hybrid' | 'satellite' | 'standard';
  plan: ComputedRoutePlan | null;
  routeName: string;
  routingProfile: RouteRoutingProfile;
  units: RouteUnitSystem;
  updatedAt: string;
  waypoints: readonly RouteWaypoint[];
}>;

function isWaypoint(value: unknown): value is RouteWaypoint {
  return isRouteCoordinate(value) && typeof (value as Partial<RouteWaypoint>).id === 'string';
}

function isDraft(value: unknown): value is RoutePlannerDraft {
  if (!value || typeof value !== 'object') return false;
  const draft = value as Partial<RoutePlannerDraft>;
  return (
    typeof draft.autoRouting === 'boolean' &&
    isRouteCoordinate(draft.center) &&
    ['hybrid', 'satellite', 'standard'].includes(draft.mapLayer ?? '') &&
    typeof draft.routeName === 'string' &&
    ['cycling-road', 'foot-walking'].includes(draft.routingProfile ?? '') &&
    ['imperial', 'metric'].includes(draft.units ?? '') &&
    typeof draft.updatedAt === 'string' &&
    Array.isArray(draft.waypoints) &&
    draft.waypoints.every(isWaypoint)
  );
}

export async function loadRoutePlannerDraft(key: string): Promise<RoutePlannerDraft | null> {
  const stored = await Storage.getItem(key);
  if (!stored) return null;
  try {
    const value: unknown = JSON.parse(stored);
    return isDraft(value) ? value : null;
  } catch {
    return null;
  }
}

export async function saveRoutePlannerDraft(key: string, draft: RoutePlannerDraft) {
  await Storage.setItem(key, JSON.stringify(draft));
}

export async function clearRoutePlannerDraft(key: string) {
  await Storage.removeItem(key);
}
